import React, { useState, useEffect } from 'react';
import { Form } from 'react-form';
import PropTypes from 'prop-types';

import { Input } from '../../../components/Form';
import Img from '../../../components/Img';
import TennisAustraliaLogin from '../../../components/TennisAustraliaLogin';
import LastUsedIndicator, { LastUsedIndicatorVariants } from '../../../components/LastUsedIndicator';

import { createReactFormValidator, requiredEmail } from '../../../utils/validation';
import { useStatsig } from '../../../components/hooks/useStatsig';
import { LastLoginMethods, resolveBadgeMethod } from '../../../utils/lastLoginMethod';
import { SocialAuthProviders } from './utils';
import styles from '../Login/login.scss';
import config from '../../../../config';

const SignInStep = ({ onContinueClick, isSubmitting, email, handleLoginWithSocial }) => {
    const { APPLE, FACEBOOK } = SocialAuthProviders;
    const { checkGate, featureGates } = useStatsig();
    const tennisAustraliaSignInEnabled = checkGate(featureGates.TENNIS_AUSTRALIA_OAUTH);

    // Advisory "Last used" hint — client-only (pre-auth traffic evaluates the gate as anonymous
    // server-side) and behind the web_login_nudge gate. Resolved after mount so the SSR and first
    // client render match (no hydration mismatch); gate-off / no-cookie stays null.
    const [lastUsed, setLastUsed] = useState(null);
    useEffect(() => {
        setLastUsed(resolveBadgeMethod(checkGate, featureGates));
    }, [checkGate, featureGates]);

    const defaultValues = {
        email: email || '',
    };

    const withBadge = (control, isLastUsed, variant, dataTestId) => {
        if (!isLastUsed) {
            return control;
        }
        return (
            <LastUsedIndicator variant={variant} data-testid={dataTestId}>
                {control}
            </LastUsedIndicator>
        );
    };

    const renderSocialAuthButton = (provider) => {
        let imageSrc = '';

        if (provider === APPLE) {
            imageSrc = '/img/v4/icon-apple-login.svg';
        } else if (provider === FACEBOOK) {
            imageSrc = '/img/v4/icon-facebook-login.svg';
        }

        const method = provider === APPLE ? LastLoginMethods.APPLE : LastLoginMethods.FACEBOOK;
        const button = (
            <button
                type="button"
                onClick={() => handleLoginWithSocial(provider)}
                className={styles.magicLinkSocialButton}
                data-testid={`magic-link-login.sign-in-step.${provider.toLowerCase()}-social-btn`}
            >
                <Img src={imageSrc} data-testid={`magic-link-login.sign-in-step.${provider.toLowerCase()}-social-icon`} />
            </button>
        );

        return withBadge(
            button,
            lastUsed === method,
            LastUsedIndicatorVariants.TILE,
            `magic-link-login.sign-in-step.${provider.toLowerCase()}-last-used`,
        );
    };

    const emailIsLastUsed = lastUsed === LastLoginMethods.PWD || lastUsed === LastLoginMethods.MAGIC_LINK;

    // The tile badge panel extends 9px above the tiles, so the row only needs Figma's extra top
    // space when a tile is really badged — a control that does not render cannot be badged, which is
    // why the TA arm repeats its own gate. With the nudge gate off the row must render exactly as it
    // did before this feature.
    const socialTileIsBadged = lastUsed === LastLoginMethods.APPLE ||
        lastUsed === LastLoginMethods.FACEBOOK ||
        (lastUsed === LastLoginMethods.TA && tennisAustraliaSignInEnabled);
    const socialContainerClassName = socialTileIsBadged
        ? `${styles.socialAuthButtonsContainer} ${styles.socialAuthButtonsBadged}`
        : styles.socialAuthButtonsContainer;

    return (
        <div>
            <p className={`${styles.magicLinkText}`} data-testid="magic-link-login.sign-in-step.title">
                Sign in with Email
            </p>
            <Form
                onSubmit={(values) => onContinueClick(values)}
                defaultValues={defaultValues}
            >
                {(formApi) => {
                    const { submitForm } = formApi;
                    const handleFormSubmit = (event) => {
                        event.preventDefault();
                        submitForm();
                    };
                    const continueButton = (
                        <button
                            type="submit"
                            className={`btn btn-primary btn-xl btn-block fw600`}
                            disabled={isSubmitting}
                            data-testid="magic-link-login.sign-in-step.continue-btn"
                        >
                            CONTINUE
                        </button>
                    );
                    return (
                        <form onSubmit={handleFormSubmit} data-testid="magic-link-login.sign-in-step.form">
                            {withBadge(
                                <Input
                                    id={`emailInput`}
                                    field={`email`}
                                    label="Email"
                                    validate={createReactFormValidator(requiredEmail)}
                                    hiddenLabel={true}
                                    disableProfanityCheck={true}
                                    themeV3
                                    data-testid="magic-link-login.sign-in-step.email-input"
                                />,
                                emailIsLastUsed,
                                LastUsedIndicatorVariants.FIELD,
                                'magic-link-login.sign-in-step.email-last-used',
                            )}

                            <div>
                                {continueButton}
                            </div>
                        </form>
                    );
                }}
            </Form>
            <div className={`${styles.tos} mb-1`}>
                <span data-testid="magic-link-login.sign-in-step.tos">By continuing, I agree to the <a href={`${config.marketingAppHost}/tos`} target="_blank" rel="noopener noreferrer" data-testid="magic-link-login.sign-in-step.tos-link">Terms of service</a>.</span>
            </div>
            <div className={'mt32 mb32'}>
                <p className={styles.magicLinkText} data-testid="magic-link-login.sign-in-step.social-heading">Or continue with</p>
                <div className={socialContainerClassName} data-badged={socialTileIsBadged}>
                    {renderSocialAuthButton(APPLE)}
                    {renderSocialAuthButton(FACEBOOK)}
                    {withBadge(
                        <TennisAustraliaLogin
                            showUpdatedButton={true}
                            data-testid="magic-link-login.sign-in-step.ta-social-btn"
                        />,
                        lastUsed === LastLoginMethods.TA && tennisAustraliaSignInEnabled,
                        LastUsedIndicatorVariants.TILE,
                        'magic-link-login.sign-in-step.ta-last-used',
                    )}
                </div>
            </div>
        </div>
    );
};

SignInStep.propTypes = {
    onContinueClick: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    email: PropTypes.string,
    handleLoginWithSocial: PropTypes.func.isRequired,
};

SignInStep.defaultProps = {
    isSubmitting: false,
    email: '',
};

export default SignInStep;
